// Converts an uploaded USDM document into the SAME clinical-intake-shaped
// JSON (study_overview.*, target_population.*, endpoints_objectives.*, ...)
// that every protocol section prompt in sectionData.js is hard-coded to
// reference via {{dotted.path}} tokens. Section prompts were never written
// to be source-agnostic — they embed those literal tokens throughout their
// instructional text — so the only reliable way to reuse them unmodified is
// to make USDM data arrive in the exact shape they already expect, then run
// it through the existing filterClinicalIntakeForSection().
//
// Two upload shapes are accepted:
//  - a standard USDM v4 instance (`{ study: { versions: [...] } }`) — this is
//    what real USDM exports (e.g. a Study Design Studio) actually produce,
//    and is the primary, best-supported path.
//  - the usdm-schema.json "catalogue" shape (`{ classes: [...] }`) — a
//    thinner fallback for hand-authored uploads.
//
// A field missing from the upload is simply left off the translated object
// — never fabricated or defaulted to a plausible-looking value. Fields with
// no reliable USDM source at all (discontinuation_rules.*, most of
// regulatory_requirements.*, background_information.*,
// template_language_fields.*) are never populated; those sections fall back
// to the gap notice below.

import templateLanguageService from '../../services/templateLanguage.service';
import {
  soaDataToTableRows,
  s9TableToLegacyPayload,
  listSoaLegacyPayloadsForExport,
} from '../../utils/soaTableExportModel';

const decode = (codeLike) => codeLike?.decode || codeLike?.code || codeLike?.name || null;

/** A StudyIdentifier's scoping Organization is a registry (ClinicalTrials.gov,
 * EudraCT, ISRCTN, ...) rather than the sponsor itself — used to tell a
 * registry number (NCT12345678) apart from the sponsor's own protocol number
 * when a study has both, per USDM's Organization.type Study Registry (C93453)
 * convention. */
function isRegistryOrganization(org) {
  if (!org) return false;
  const label = `${decode(org.type) || ''} ${org.name || ''} ${org.label || ''}`.toLowerCase();
  return /registry|clinicaltrials|ct-?gov|isrctn|eudract|who ictrp/.test(label);
}

function getLatestStudyVersion(usdmDoc) {
  const versions = usdmDoc?.study?.versions;
  if (!Array.isArray(versions) || versions.length === 0) return null;
  return versions[versions.length - 1];
}

function getPrimaryStudyDesign(studyVersion) {
  const designs = studyVersion?.studyDesigns;
  if (!Array.isArray(designs) || designs.length === 0) return null;
  return designs[0];
}

/**
 * Flattens every ExtensionAttribute (including nested valueExtensionClass
 * trees) into { [urlFragmentAfterHash]: value }. Dizzaroo-produced USDM
 * exports key their extension attributes with the same dotted-path
 * "spine" vocabulary as usdm-schema.json (e.g. "safety.committee.structure",
 * "treatment.regimen.<id>.discontinuation"), so this gives cheap access to
 * a lot of otherwise-unreachable narrative content. JSON-encoded valueString
 * fields are parsed where possible.
 */
function flattenExtensionAttributes(attrs, out = {}) {
  if (!Array.isArray(attrs)) return out;
  for (const attr of attrs) {
    if (!attr) continue;
    const key = String(attr.url || '').split('#')[1];
    if (key) {
      let value =
        attr.valueString ?? attr.valueInteger ?? attr.valueBoolean ?? attr.valueDecimal ?? null;
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          // not JSON — keep as plain string
        }
      }
      // First value wins. Dizzaroo exports repeat some keys in a trailing
      // decisionRationale class (e.g. decisionRationale#schedule.soa.grid holds
      // only {rationale}); letting it overwrite dropped the real SoA grid and
      // the provisional indication.
      if (value !== null && value !== undefined && value !== '' && !(key in out)) out[key] = value;
    }
    if (attr.valueExtensionClass?.extensionAttributes) {
      flattenExtensionAttributes(attr.valueExtensionClass.extensionAttributes, out);
    }
  }
  return out;
}

/**
 * Design Studio extension classes on studyVersion, grouped by the protocol
 * content they carry. Each source is [className, optional key pattern]; a
 * key goes to the first group that claims it. Section prompts point at these
 * groups as {{facts.designDetail.<group>}} (or one key of a group as
 * {{facts.designDetail.<group>["key"]}}), and each section is sent only the
 * groups its prompt points at — see buildUsdmSectionStudyData.
 */
const DESIGN_DETAIL_GROUPS = {
  // Ready-written arguments keyed by Design Studio section number ("2.1",
  // "4.2.3", ...). Its "10.8" is Sample Size Determination.
  studyRationale: [['studyRationale']],
  trialSchema: [['trialSchema']],
  committees: [['safetyGovernance', /^committee\./]],
  // AESI, risk mitigation, stopping rules, dose holds/reductions, conmeds.
  safety: [['safetyGovernance'], ['doseModificationRules'], ['concomitantMedications'], ['conMedRules']],
  // Per-regimen dose modification, discontinuation, conmed and combination rules.
  treatmentRules: [['treatmentRules']],
  // Study parts, dose escalation, DLT criteria, dose levels, expansion cohorts.
  doseEscalation: [['doseEscalation'], ['studyArchitecture'], ['studyDesignIntent']],
  statistics: [['statisticalDesign'], ['multiplicityStrategy'], ['randomization']],
  endpoints: [['protocolDetail', /^endpoint\./], ['endpointPrecedent'], ['proStrategy']],
  // The final population: group properties, contraception definitions.
  population: [
    ['protocolDetail', /^population\.final\./],
    ['cohortEligibility', /\.(applies_to|rationale)$/],
    ['eligibilityDefinitions'],
    ['legacyPopulation'],
  ],
  // Not pointed at by any prompt: Design Studio's working population notes
  // (superseded by the final population; facts.provisional surfaces them when
  // structured data is missing) and the per-cohort criteria with metadata,
  // which population.eligibilityText already carries.
  populationWorkingNotes: [
    ['provisionalPopulation'],
    ['protocolDetail', /^population\./],
    ['cohortEligibility'],
  ],
  screeningTests: [['companionDiagnostic']],
  translational: [['translational'], ['operationalModel'], ['pkStrategy']],
  regulatory: [['regulatoryStrategy']],
  productProfile: [
    ['targetProductProfile'],
    ['developmentContext'],
    ['pharmacology'],
    ['designIntent'],
    ['protocolDetail', /^(rationale|outline|design)\./],
  ],
  operations: [['protocolReference'], ['protocolDetail', /^(operational|schedule|admin)\./]],
  // Label and literature sources behind each regimen.
  sources: [['designProvenance']],
};

// Carried elsewhere: the SoA grid as soaTables, decision rationale as designDetail.rationale.
const DESIGN_DETAIL_EXCLUDED_CLASSES = new Set(['soaGrid', 'decisionRationale']);

/** { className: { key: value } } for every ExtensionAttribute under `attrs`, JSON values parsed. */
function extensionValuesByClass(attrs) {
  const byClass = {};
  const walk = (list, inheritedClass) => {
    for (const attr of list || []) {
      const url = String(attr?.url || '');
      const className = inheritedClass || url.split('/').pop().split('#')[0];
      const key = url.split('#')[1];
      let value = attr?.valueString ?? attr?.valueInteger ?? attr?.valueBoolean ?? attr?.valueDecimal ?? null;
      if (key && value !== null && value !== '') {
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (e) {
            // not JSON — keep as plain string
          }
        }
        const bucket = (byClass[className] = byClass[className] || {});
        if (!(key in bucket)) bucket[key] = value;
      }
      if (attr?.valueExtensionClass?.extensionAttributes) {
        walk(attr.valueExtensionClass.extensionAttributes, className);
      }
    }
  };
  walk(attrs, null);
  return byClass;
}

/**
 * Design Studio content that standard USDM has no field for, as named groups
 * (see DESIGN_DETAIL_GROUPS). Classes no group names are kept under `other`
 * rather than dropped. `rationale` holds each decision's justification text,
 * keyed like the decision itself.
 */
function extractDesignDetail(studyVersion) {
  const byClass = extensionValuesByClass(studyVersion?.extensionAttributes);
  const detail = {};
  const claimed = new Set();
  for (const [group, sources] of Object.entries(DESIGN_DETAIL_GROUPS)) {
    for (const [className, keyPattern] of sources) {
      for (const [key, value] of Object.entries(byClass[className] || {})) {
        if (keyPattern && !keyPattern.test(key)) continue;
        if (claimed.has(`${className}#${key}`)) continue;
        claimed.add(`${className}#${key}`);
        (detail[group] = detail[group] || {})[key] = value;
      }
    }
  }
  for (const [className, values] of Object.entries(byClass)) {
    if (DESIGN_DETAIL_EXCLUDED_CLASSES.has(className)) continue;
    for (const [key, value] of Object.entries(values)) {
      if (claimed.has(`${className}#${key}`)) continue;
      detail.other = detail.other || {};
      (detail.other[className] = detail.other[className] || {})[key] = value;
    }
  }
  if (detail.trialSchema) detail.trialSchema = describeTrialSchema(detail.trialSchema);
  const rationale = {};
  for (const [key, value] of Object.entries(byClass.decisionRationale || {})) {
    const text = value && typeof value === 'object' ? value.rationale : value;
    if (text) rationale[key] = String(text);
  }
  detail.rationale = rationale;
  return detail;
}

/**
 * The trial schema as drawn in Design Studio: the figure (parts, arms, dose
 * levels, populations), each box's visible lines, and the connections. Lines
 * the schema hides and its layout/rendering settings are left out — they are
 * not part of the schema the reader sees.
 */
function describeTrialSchema(schema) {
  const boxes = {};
  for (const [id, box] of Object.entries(schema.boxes || {})) {
    if (!box || typeof box !== 'object') continue;
    const lines = (box.lines || []).filter((l) => l && l.shown !== false && l.text).map((l) => l.text);
    if (lines.length) boxes[id] = { label: box.label || id, lines };
  }
  return {
    ...(schema.figure ? { figure: schema.figure } : {}),
    ...(Object.keys(boxes).length ? { boxes } : {}),
    ...(schema.connections ? { connections: schema.connections } : {}),
  };
}

/** Value of the first ExtensionAttribute in `attrs` (one level only) whose url ends with `urlSuffix`. */
function extensionAttrValue(attrs, urlSuffix) {
  const attr = (attrs || []).find((a) => String(a?.url || '').endsWith(urlSuffix));
  if (!attr) return null;
  return attr.valueString ?? attr.valueInteger ?? attr.valueDecimal ?? attr.valueBoolean ?? null;
}

/**
 * Design Studio's schedule narrative (visit framework, tumour assessment
 * cadence, safety lab timing, PK sampling ...) sits on the schedule
 * timelines, not on studyVersion. The trailing decisionRationale class reuses
 * the same keys for the decision's justification, so reading studyVersion
 * alone gave the rationale in place of the schedule itself.
 */
function extractScheduleNarrative(design) {
  const out = {};
  for (const timeline of design?.scheduleTimelines || []) {
    for (const attr of timeline?.extensionAttributes || []) {
      const url = String(attr?.url || '');
      if (!url.includes('/scheduleNarrative#')) continue;
      const key = url.split('#')[1];
      if (key && attr.valueString && !(key in out)) out[key] = attr.valueString;
    }
  }
  return out;
}

/**
 * Arm names per study intervention. USDM links them only indirectly: an arm's
 * study cells hold elements, and an element names its interventions.
 */
function armNamesByIntervention(design) {
  const elementsById = new Map((design?.elements || []).map((e) => [e.id, e]));
  const armNameById = new Map((design?.arms || []).map((a) => [a.id, a.label || a.name]));
  const out = new Map();
  for (const cell of design?.studyCells || []) {
    const armName = armNameById.get(cell.armId);
    if (!armName) continue;
    for (const elementId of cell.elementIds || []) {
      for (const interventionId of elementsById.get(elementId)?.studyInterventionIds || []) {
        if (!out.has(interventionId)) out.set(interventionId, []);
        if (!out.get(interventionId).includes(armName)) out.get(interventionId).push(armName);
      }
    }
  }
  return out;
}

/**
 * Readable drug name for an AdministrableProduct. Design Studio names products
 * with an id-like key ("bgb_43395") and carries the display name as
 * design.drug.<key>.display_name.
 */
function describeProduct(product, spineExt) {
  const key = String(product?.id || '').replace(/^AdministrableProduct_/, '');
  return {
    name: spineExt?.[`design.drug.${key}.display_name`] || product?.label || product?.name || null,
    doseForm: decode(product?.administrableDoseForm?.standardCode) || decode(product?.administrableDoseForm),
    pharmacologicClass: decode(product?.pharmacologicClass),
    designation: decode(product?.productDesignation),
  };
}

/** Epochs in study order, following previousId/nextId, then any left over in array order. */
function orderEpochs(epochs) {
  const byId = new Map(epochs.map((e) => [e.id, e]));
  const ordered = [];
  let current = epochs.find((e) => !e.previousId || !byId.has(e.previousId));
  while (current && !ordered.includes(current)) {
    ordered.push(current);
    current = byId.get(current.nextId);
  }
  return [...ordered, ...epochs.filter((e) => !ordered.includes(e))];
}

/**
 * Study periods: each epoch with its planned duration (Design Studio's
 * epochDuration extension) and the elements run in it, with their start and
 * end rules and the arms that run them.
 */
function extractStudyPeriods(design) {
  const elementsById = new Map((design?.elements || []).map((e) => [e.id, e]));
  const armNameById = new Map((design?.arms || []).map((a) => [a.id, a.label || a.name]));
  return orderEpochs(design?.epochs || []).map((epoch) => {
    const elements = new Map(); // element id -> entry, in first-seen order
    for (const cell of design?.studyCells || []) {
      if (cell.epochId !== epoch.id) continue;
      for (const elementId of cell.elementIds || []) {
        const element = elementsById.get(elementId);
        if (!element) continue;
        if (!elements.has(element.id)) {
          elements.set(element.id, {
            name: element.label || element.name,
            start: element.transitionStartRule?.text || null,
            end: element.transitionEndRule?.text || null,
            arms: [],
          });
        }
        const entry = elements.get(element.id);
        const armName = armNameById.get(cell.armId);
        if (armName && !entry.arms.includes(armName)) entry.arms.push(armName);
      }
    }
    return {
      name: epoch.label || epoch.name,
      type: decode(epoch.type),
      plannedDuration: extensionAttrValue(epoch.extensionAttributes, '#plannedDuration'),
      elements: [...elements.values()],
    };
  });
}

function extractStudyDuration(design) {
  const timelines = design?.scheduleTimelines || [];
  const timeline =
    timelines.find((t) => t?.mainTimeline && t.plannedDuration?.text) || timelines.find((t) => t?.plannedDuration?.text);
  const duration = timeline?.plannedDuration;
  if (!duration) return null;
  return {
    text: duration.text,
    willVary: Boolean(duration.durationWillVary),
    reason: duration.reasonDurationWillVary || null,
  };
}

/**
 * Planned age as protocol text. Design Studio writes "18 years or older" as
 * 18-100 years, because USDM needs an upper bound, and records that it did so
 * in the export report's conventions; honour that rather than print 100.
 */
function describePlannedAge(range, raw) {
  const min = range?.minValue?.value;
  const max = range?.maxValue?.value;
  if (min == null && max == null) return null;
  const unitName = String(decode(range?.minValue?.unit?.standardCode) || decode(range?.maxValue?.unit?.standardCode) || 'Year');
  const unit = /s$/i.test(unitName) ? unitName.toLowerCase() : `${unitName.toLowerCase()}s`;
  const conventions = raw?.['dizzaroo:exportReport']?.conventions || [];
  const noUpperLimit = conventions.some((c) => /plannedAge/.test(JSON.stringify(c)) && /no upper age limit/i.test(JSON.stringify(c)));
  if (min != null && (max == null || noUpperLimit)) return `${min} ${unit} or older`;
  if (min == null) return `up to ${max} ${unit}`;
  return `${min} to ${max} ${unit}`;
}

function extractCohorts(design) {
  const indicationById = new Map((design?.indications || []).map((i) => [i.id, i.description || i.name]));
  return (design?.population?.cohorts || []).map((cohort) => {
    const name = cohort.label || cohort.name;
    return {
      name,
      // `text` is the full value; `name` can be cut short but may carry the
      // property label, e.g. "Cns status: No active ... (Part 1a ...)".
      characteristics: (cohort.characteristics || [])
        .map((ch) => {
          const value = String(ch?.text || ch?.name || '');
          const label = String(ch?.name || '').includes(':') ? String(ch.name).split(':')[0].trim() : '';
          return label && !value.startsWith(label) ? `${label}: ${value}` : value;
        })
        .filter(Boolean),
      indications: (cohort.indicationIds || []).map((id) => indicationById.get(id)).filter(Boolean),
    };
  });
}

function extractArmDetails(studyVersion, design) {
  const interventionNameById = new Map((studyVersion?.studyInterventions || []).map((iv) => [iv.id, iv.label || iv.name]));
  const regimensByArm = new Map();
  for (const [interventionId, armNames] of armNamesByIntervention(design)) {
    for (const armName of armNames) {
      if (!regimensByArm.has(armName)) regimensByArm.set(armName, []);
      const regimen = interventionNameById.get(interventionId);
      if (regimen) regimensByArm.get(armName).push(regimen);
    }
  }
  const cohortNameById = new Map((design?.population?.cohorts || []).map((c) => [c.id, c.label || c.name]));
  return (design?.arms || []).map((arm) => {
    const name = arm.label || arm.name;
    return {
      name,
      type: decode(arm.type),
      description: arm.description || null,
      regimens: regimensByArm.get(name) || [],
      cohorts: (arm.populationIds || []).map((id) => cohortNameById.get(id)).filter(Boolean),
    };
  });
}

/** Estimands with their references resolved to readable names. */
function extractEstimandDetails(studyVersion, design) {
  const endpointById = new Map(
    (design?.objectives || []).flatMap((o) => (o.endpoints || []).map((e) => [e.id, e.text || e.name])),
  );
  const populationById = new Map((design?.analysisPopulations || []).map((p) => [p.id, p.label || p.name]));
  const interventionNameById = new Map((studyVersion?.studyInterventions || []).map((iv) => [iv.id, iv.label || iv.name]));
  return (design?.estimands || []).map((est) => ({
    name: est.label || est.name || null,
    description: est.description || null,
    variable: endpointById.get(est.variableOfInterestId) || null,
    analysisPopulation: populationById.get(est.analysisPopulationId) || null,
    populationSummary: est.populationSummary || null,
    interventions: (est.interventionIds || []).map((id) => interventionNameById.get(id)).filter(Boolean),
    intercurrentEvents: (est.intercurrentEvents || []).map((ice) => ({
      event: ice.text || ice.name,
      strategy: ice.strategy || null,
    })),
  }));
}

function objectiveLevelBucket(objective) {
  const levelDecode = String(decode(objective?.level) || '').toLowerCase();
  const tierExt = (objective?.extensionAttributes || []).find((a) =>
    String(a?.url || '').endsWith('#tier'),
  )?.valueString;
  if (tierExt === 'key_secondary') return 'key_secondary';
  if (levelDecode.includes('primary')) return 'primary';
  if (levelDecode.includes('exploratory')) return 'exploratory';
  if (levelDecode.includes('secondary')) return 'secondary';
  return 'other';
}

// Label for criteria that apply to the whole trial population rather than
// one cohort. Section 5's prompt refers to these labels by name.
const ALL_PARTICIPANTS_LABEL = 'All participants';

/**
 * Inclusion/exclusion criteria text, grouped by the cohort each criterion
 * applies to (population.criterionIds = all participants,
 * population.cohorts[].criterionIds = that cohort only). Without the grouping,
 * near-identical per-cohort criteria (e.g. three washout rules with different
 * intervals) reach the model as one flat list and get merged, losing the
 * cohort-specific values. Studies with no cohort links keep the flat list.
 * Output keeps the "Inclusion Criteria:" / "Exclusion Criteria:" headers and
 * one "- " line per criterion. The counts are returned alongside so
 * getUsdmDataCoverage does not have to parse them back out of the text.
 */
function extractEligibility(studyVersion, design) {
  const items = studyVersion?.eligibilityCriterionItems || [];
  const byId = Object.fromEntries(items.map((i) => [i.id, i.text]));
  const criteria = design?.eligibilityCriteria || [];
  const cohorts = (design?.population?.cohorts || []).filter((c) => c?.criterionIds?.length);
  const groupLabelFor = (criterionId) => {
    const names = cohorts
      .filter((c) => c.criterionIds.includes(criterionId))
      .map((c) => c.label || c.name || c.id);
    return names.length ? `${names.join(' and ')} only` : ALL_PARTICIPANTS_LABEL;
  };

  // Map preserves first-seen order; "All participants" always comes first.
  const inclusion = new Map([[ALL_PARTICIPANTS_LABEL, []]]);
  const exclusion = new Map([[ALL_PARTICIPANTS_LABEL, []]]);
  for (const c of criteria) {
    const text = byId[c.criterionItemId];
    if (!text) continue;
    const cat = String(decode(c.category) || '').toLowerCase();
    const groups = cat.includes('exclusion') ? exclusion : inclusion;
    const label = groupLabelFor(c.id);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(text);
  }

  const renderGroups = (groups) => {
    const nonEmpty = [...groups].filter(([, texts]) => texts.length);
    const bullets = (texts) => texts.map((t) => `- ${t}`).join('\n');
    if (nonEmpty.length === 1 && nonEmpty[0][0] === ALL_PARTICIPANTS_LABEL) return bullets(nonEmpty[0][1]);
    return nonEmpty.map(([label, texts]) => `${label}:\n${bullets(texts)}`).join('\n');
  };
  const count = (groups) => [...groups.values()].reduce((n, texts) => n + texts.length, 0);

  const inclusionCount = count(inclusion);
  const exclusionCount = count(exclusion);
  const parts = [];
  if (inclusionCount) parts.push(`Inclusion Criteria:\n${renderGroups(inclusion)}`);
  if (exclusionCount) parts.push(`Exclusion Criteria:\n${renderGroups(exclusion)}`);
  return { text: parts.join('\n\n'), inclusionCount, exclusionCount };
}

function extractTreatmentTable(studyVersion, design, spineExt) {
  const productsById = new Map((studyVersion?.administrableProducts || []).map((p) => [p.id, p]));
  const armsByIntervention = armNamesByIntervention(design);
  const rows = [];
  for (const iv of studyVersion?.studyInterventions || []) {
    const base = {
      arm: (armsByIntervention.get(iv.id) || []).join('; ') || null,
      intervention: iv.name,
      role: decode(iv.role),
    };
    const administrations = iv.administrations || [];
    if (!administrations.length) {
      rows.push(base);
      continue;
    }
    for (const admin of administrations) {
      const product = productsById.get(admin.administrableProductId);
      const attrs = admin.extensionAttributes;
      rows.push({
        ...base,
        product: product ? describeProduct(product, spineExt).name : null,
        doseForm: product ? describeProduct(product, spineExt).doseForm : null,
        dose: admin.dose ? `${admin.dose.value ?? ''} ${decode(admin.dose.unit?.standardCode) || ''}`.trim() : null,
        route: decode(admin.route?.standardCode),
        frequency: decode(admin.frequency?.standardCode) || admin.description || null,
        // Design Studio's dosingSchedule extension: e.g. "QD 21/7", days 1-21 of a 28-day cycle.
        schedule: extensionAttrValue(attrs, '#scheduleText'),
        dosingDays: extensionAttrValue(attrs, '#dosingDays'),
        cycleLengthDays: extensionAttrValue(attrs, '#cycleLengthDays'),
        duration: admin.duration?.text || null,
      });
    }
  }
  return rows;
}

function extractIntercurrentEventsHandling(design) {
  const rows = [];
  for (const est of design?.estimands || []) {
    for (const ice of est.intercurrentEvents || []) {
      // ice.name is generic ("... intercurrent event 1"); ice.text is the event itself.
      rows.push({ estimand: est.label || est.name || null, event: ice.text || ice.name, strategy: ice.strategy || null });
    }
  }
  return rows;
}

/**
 * Reconstructs the real encounter × activity Schedule of Activities grid
 * from a USDM v4 instance's ScheduleTimeline — this is the one piece of
 * "1.3 SCHEDULE OF ACTIVITIES" that should come from the uploaded catalogue
 * directly rather than an LLM narrative, since USDM actually models it as
 * structured data (unlike most other protocol sections, which are prose USDM
 * has no equivalent structured field for).
 *
 * design.activities[]/design.encounters[] alone (used elsewhere in this file
 * for narrative context) only carry names — the relationship between them
 * lives one level up, in design.scheduleTimelines[].instances[]
 * (ScheduledActivityInstance), each of which has an encounterId plus an
 * activityIds[] list. Reads design directly rather than the already-mapped
 * `activities`/`encounters` facts elsewhere in this file, since those strip
 * the .id needed to join here.
 *
 * Returns null (not a fabricated/empty table) when the upload has no real
 * schedule timeline data — e.g. the thinner usdm-schema.json "catalogue"
 * shape never has ScheduleTimeline/ScheduledActivityInstance at all.
 */
function extractScheduleOfActivities(design, bcCategories) {
  const timelines = Array.isArray(design?.scheduleTimelines) ? design.scheduleTimelines : [];
  if (!timelines.length) return null;

  const timeline = timelines.find((t) => t?.mainTimeline) || timelines[0];
  const instances = (timeline?.instances || []).filter(
    (i) => i?.encounterId && Array.isArray(i?.activityIds) && i.activityIds.length
  );
  if (!instances.length) return null;

  const encountersById = new Map((design.encounters || []).map((e) => [e.id, e]));
  const activitiesById = new Map((design.activities || []).map((a) => [a.id, a]));
  // bcCategories lives on studyVersion, as a sibling of studyDesigns — NOT
  // nested inside design itself (verified against a real uploaded catalogue:
  // design.bcCategories is undefined there even though every Activity's
  // bcCategoryIds correctly references BiomedicalConceptCategory ids that
  // only resolve via studyVersion.bcCategories).
  const categoryNameById = new Map((bcCategories || []).map((c) => [c.id, c.name || c.label]));

  // Column order: encounters in the order they're first referenced by the
  // timeline (matches visit order), not design.encounters' own array order.
  const encounterOrder = [];
  const seenEncounterIds = new Set();
  for (const inst of instances) {
    if (!seenEncounterIds.has(inst.encounterId)) {
      seenEncounterIds.add(inst.encounterId);
      encounterOrder.push(inst.encounterId);
    }
  }

  const occursAtEncounters = new Map(); // activityId -> Set(encounterId)
  for (const inst of instances) {
    for (const activityId of inst.activityIds) {
      if (!occursAtEncounters.has(activityId)) occursAtEncounters.set(activityId, new Set());
      occursAtEncounters.get(activityId).add(inst.encounterId);
    }
  }

  // Row order: design.activities' own defined order (stable, matches how the
  // study actually lists them) — filtered to only activities the timeline
  // actually schedules at at least one encounter.
  const activityOrder = (design.activities || [])
    .map((a) => a.id)
    .filter((id) => id && occursAtEncounters.has(id));
  if (!activityOrder.length || !encounterOrder.length) return null;

  const visits = encounterOrder.map((encId) => ({
    period: encountersById.get(encId)?.name || encountersById.get(encId)?.label || 'Visit',
    day: '',
  }));

  // Row groups: bucket activities by their biomedical concept category
  // (design.bcCategories, joined via each Activity's bcCategoryIds[0]) —
  // the same grouping (e.g. "Safety — Laboratory Tests", "Cardiac Safety")
  // the study's own SoA table uses. soaDataToTableRows renders one banner
  // row per `sections[]` entry, so this gives the familiar layered/grouped
  // row pattern instead of one flat "Procedures" list. Activities with no
  // resolvable category fall back into a single trailing "Procedures" group
  // rather than being dropped.
  const groupsInOrder = [];
  const groupByTitle = new Map();
  for (const actId of activityOrder) {
    const categoryId = (activitiesById.get(actId)?.bcCategoryIds || [])[0] || null;
    const groupTitle = (categoryId && categoryNameById.get(categoryId)) || 'Procedures';
    if (!groupByTitle.has(groupTitle)) {
      const group = { title: groupTitle, activityIds: [] };
      groupByTitle.set(groupTitle, group);
      groupsInOrder.push(group);
    }
    groupByTitle.get(groupTitle).activityIds.push(actId);
  }

  const sections = groupsInOrder.map((group) => ({
    title: group.title,
    activities: group.activityIds.map((actId) => {
      const encounterIdsForActivity = occursAtEncounters.get(actId);
      return {
        name: activitiesById.get(actId)?.name || activitiesById.get(actId)?.label || 'Activity',
        visits: encounterOrder
          .map((encId, idx) => (encounterIdsForActivity.has(encId) ? idx : -1))
          .filter((idx) => idx >= 0),
      };
    }),
  }));

  return { visits, sections, footnotes: [] };
}

/**
 * Renders extractScheduleOfActivities()'s output as an HTML <table> string —
 * usdmProtocolSectionData.js's own prompt for this section already asks the
 * LLM to produce "an HTML table mapping trial assessments/procedures to the
 * visits or periods at which they are performed", so matching that exact
 * output shape means whatever DocumentEditor rendering/export already
 * handles this section's normal (LLM-generated) content handles this
 * directly-built table identically — no new format for anything downstream
 * to learn. Uses the same header/row model (soaDataToTableRows) the real SoA
 * editor and its own Word/PDF exports already use, so the column/row logic
 * is proven, not reinvented.
 *
 * Returns '' when there's no real schedule data to render (see
 * extractScheduleOfActivities) — the caller should fall back to the normal
 * LLM-prompt-based generation for this section in that case.
 */
export function buildScheduleOfActivitiesHtml(soaData) {
  if (!soaData) return '';
  // Accepts the rich {tables: [...]} shape (every schedule — e.g. Part 1a,
  // Part 1b and a PK table) or one legacy {visits, sections} payload.
  const payloads = listSoaLegacyPayloadsForExport(soaData);
  const labelByTableId = new Map((soaData.tables || []).map((t) => [t?.tableId, t?.label || t?.title]));

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r?\n/g, '<br>');

  const parts = [];
  for (const payload of payloads) {
    // Key Notes carries each activity's own footnotes; the rest follow the table.
    const { header, rows, footnotesBlock } = soaDataToTableRows(payload);
    if (!header.length || !rows.length) continue;
    const label = payloads.length > 1 ? labelByTableId.get(payload.sourceTableId) : null;
    const headRow = `<tr>${header.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr>`;
    const bodyRows = rows
      .map((row) => `<tr>${header.map((col) => `<td>${escapeHtml(row[col])}</td>`).join('')}</tr>`)
      .join('');
    parts.push(
      `${label ? `<p><strong>${escapeHtml(label)}</strong></p>` : ''}<table><thead>${headRow}</thead><tbody>${bodyRows}</tbody></table>${(footnotesBlock || []).map((note) => `<p>${escapeHtml(note)}</p>`).join('')}`,
    );
  }
  return parts.join('');
}

/** The schedule data for Section 1.3: every authored SoA table when the export has them. */
export function scheduleOfActivitiesDataFromFacts(facts) {
  if (facts?.soaTables?.length) return { tables: facts.soaTables };
  return facts?.scheduleOfActivities || null;
}

// Facts every section receives; the rest only when its prompt points at them.
const ALWAYS_SENT_FACTS = ['identity', 'design', 'dataGaps', 'designDecisions', 'provisional', 'exportNotes'];
// Never sent as section data: spineExt is the raw form of designDetail, and the
// SoA grids are rendered directly in Section 1.3.
const NEVER_SENT_FACTS = new Set(['spineExt', 'soaTables', 'scheduleOfActivities', 'designDetail']);

/**
 * The structured study data for one section: the facts its prompt points at
 * ({{facts.<key>}}), the designDetail groups it points at
 * ({{facts.designDetail.<group>}} or {{facts.designDetail.<group>["key"]}}),
 * and the decision rationale behind the decisions in those groups.
 *
 * Every section used to receive the whole fact set (110–140k tokens, mostly
 * raw extension data the prompt never pointed at). The global context still
 * carries the readable summary of the whole study to every section.
 */
export function buildUsdmSectionStudyData(facts, sectionPrompt = '') {
  if (!facts) return null;
  const prompt = String(sectionPrompt || '');
  const data = {};
  const referenced = new Set([...prompt.matchAll(/facts\.(\w+)/g)].map((m) => m[1]));
  for (const key of [...ALWAYS_SENT_FACTS, ...referenced]) {
    if (NEVER_SENT_FACTS.has(key) || !(key in facts) || key in data) continue;
    data[key] = facts[key];
  }

  const detail = facts.designDetail || {};
  const picked = {};
  for (const [, group, key] of prompt.matchAll(/facts\.designDetail\.(\w+)(?:\["([^"]+)"\])?/g)) {
    if (group === 'rationale' || !detail[group]) continue;
    if (key === undefined) picked[group] = { ...(picked[group] || {}), ...detail[group] };
    else if (detail[group][key] !== undefined) picked[group] = { ...(picked[group] || {}), [key]: detail[group][key] };
  }
  // {{facts.designDetail.rationale}} asks for the justification of the
  // decisions this section is sent. Rationale shares its keys with the
  // decisions; match on the first two key segments. Eligibility rationale is
  // left out — Study Rationale 5.1 covers it and it is the largest block.
  if (/facts\.designDetail\.rationale\b/.test(prompt)) {
    const prefix = (k) => k.split('.').slice(0, 2).join('.');
    const prefixes = new Set(Object.values(picked).flatMap((g) => Object.keys(g)).map(prefix));
    const rationale = Object.fromEntries(
      Object.entries(detail.rationale || {}).filter(
        ([k]) => prefixes.has(prefix(k)) && !k.startsWith('population.eligibility.'),
      ),
    );
    if (Object.keys(rationale).length) picked.rationale = rationale;
  }
  if (Object.keys(picked).length) data.designDetail = picked;
  return data;
}

/**
 * Context for the validation and regeneration step. They must see the same
 * study data the section was generated from; with only the global context
 * they flagged real study data (committees, event counts, dose levels) as
 * invented, and regeneration then removed it.
 */
export function buildUsdmValidationContext(context, studyData) {
  if (!studyData) return context;
  return `${context}\n\nSTUDY DEFINITION DATA (the structured study data this section was generated from; facts in it are not invented):\n${JSON.stringify(studyData)}`;
}

/**
 * Extracts a normalized fact set from a standard USDM v4 instance document.
 * Mirrors etmf-client/src/sap/utils/usdmToSapContext.js's extraction style,
 * scoped to the fields the 16 protocol section prompts actually reference.
 */
function extractFactsFromUsdmInstance(usdmDoc) {
  const studyVersion = getLatestStudyVersion(usdmDoc);
  const design = getPrimaryStudyDesign(studyVersion);
  if (!studyVersion || !design) return null;

  // Dizzaroo-produced exports put every "class_*" extension (soaGrid,
  // safetyGovernance, randomization, developmentContext, ...) on studyVersion
  // (a sibling of studyDesigns), not nested inside the design object itself —
  // verified against a real upload, where design.extensionAttributes is an
  // empty array while studyVersion.extensionAttributes holds all 20 classes.
  // Reading only design.extensionAttributes silently starved every spineExt-
  // derived fact (committeeStructure, riskMitigationTriggers, the SoA grid,
  // etc.) for real Dizzaroo exports even though the data was present.
  // The timeline schedule narrative goes in first so it wins over the
  // decisionRationale entries that reuse its keys (first value wins).
  const scheduleNarrative = extractScheduleNarrative(design);
  const spineExt = { ...scheduleNarrative };
  flattenExtensionAttributes(design.extensionAttributes, spineExt);
  flattenExtensionAttributes(studyVersion.extensionAttributes, spineExt);
  const designDetail = extractDesignDetail(studyVersion);
  const eligibility = extractEligibility(studyVersion, design);

  const objectivesByBucket = { primary: [], secondary: [], key_secondary: [], exploratory: [] };
  for (const obj of design.objectives || []) {
    const bucket = objectiveLevelBucket(obj);
    if (objectivesByBucket[bucket]) objectivesByBucket[bucket].push(obj);
    else objectivesByBucket.secondary.push(obj);
  }

  const arms = design.arms || [];
  const investigationalArm = arms.find((a) => /experimental|investigational/i.test(decode(a.type) || ''));
  const controlArm = arms.find((a) => /comparator|control|placebo/i.test(decode(a.type) || ''));

  const narrativeByName = Object.fromEntries(
    (studyVersion.narrativeContentItems || []).map((n) => [String(n.name || '').toLowerCase(), n.text]),
  );

  // Title-page identity needs a sharper split than the plain "first
  // identifier wins" used elsewhere in this file: a study commonly has both
  // its sponsor protocol number and a public registry number (NCT, EudraCT,
  // ...) as separate StudyIdentifiers, scoped to different Organizations.
  const identifiers = studyVersion.studyIdentifiers || [];
  const organizationsById = new Map((studyVersion.organizations || []).map((o) => [o.id, o]));
  const registryIdentifier = identifiers.find((i) => isRegistryOrganization(organizationsById.get(i.scopeId)));
  const sponsorIdentifier =
    identifiers.find((i) => !isRegistryOrganization(organizationsById.get(i.scopeId))) || identifiers[0];
  const sponsorOrg =
    (sponsorIdentifier && organizationsById.get(sponsorIdentifier.scopeId)) ||
    (studyVersion.organizations || []).find((o) => !isRegistryOrganization(o)) ||
    (studyVersion.organizations || [])[0];

  return {
    identity: {
      studyName: usdmDoc.study?.name || null,
      officialTitle: (studyVersion.titles || []).find((t) => /official/i.test(decode(t.type) || ''))?.text || null,
      briefTitle: (studyVersion.titles || []).find((t) => /brief|short/i.test(decode(t.type) || ''))?.text || null,
      acronym: (studyVersion.titles || []).find((t) => /acronym/i.test(decode(t.type) || ''))?.text || null,
      protocolNumber: sponsorIdentifier?.text || null,
      nctNumber: registryIdentifier?.text || null,
      sponsorName: sponsorOrg?.name || null,
      sponsorAddress: sponsorOrg?.legalAddress?.text || (sponsorOrg?.legalAddress?.lines || []).join(', ') || null,
      // The Study Design Studio record's own version and approval date —
      // not the protocol version or protocol date.
      designVersion: studyVersion.versionIdentifier || null,
      designApprovalDate:
        (studyVersion.dateValues || []).find((d) => /approv/i.test(decode(d.type) || ''))?.dateValue || null,
    },
    roles: (studyVersion.roles || []).map((r) => ({
      role: decode(r.code) || r.name || null,
      label: r.label || null,
      description: r.description || null,
      organizations: (r.organizationIds || []).map((id) => organizationsById.get(id)?.name).filter(Boolean),
    })),
    design: {
      studyType: decode(design.studyType),
      studyPhase: decode(design.studyPhase?.standardCode) || decode(design.studyPhase),
      model: decode(design.model),
      // AliasCode, same as studyPhase — the real value is under .standardCode.
      blindingSchema: decode(design.blindingSchema?.standardCode) || decode(design.blindingSchema),
      intentTypes: (design.intentTypes || []).map(decode).filter(Boolean),
      rationale: design.rationale || null,
    },
    arms,
    armDetails: extractArmDetails(studyVersion, design),
    investigationalArmLabel: investigationalArm?.label || investigationalArm?.name || null,
    controlArmLabel: controlArm?.label || controlArm?.name || null,
    interventionNames: (studyVersion.studyInterventions || []).map((iv) => iv.name).filter(Boolean),
    studyPeriods: extractStudyPeriods(design),
    studyDuration: extractStudyDuration(design),
    population: {
      description: design.population?.description || null,
      plannedEnrollmentNumber: design.population?.plannedEnrollmentNumber?.value ?? null,
      plannedAge: describePlannedAge(design.population?.plannedAge, usdmDoc),
      plannedSex: (design.population?.plannedSex || []).map(decode).filter(Boolean),
      includesHealthySubjects: design.population?.includesHealthySubjects ?? null,
      cohorts: extractCohorts(design),
      eligibilityText: eligibility.text,
      inclusionCount: eligibility.inclusionCount,
      exclusionCount: eligibility.exclusionCount,
    },
    objectivesByBucket,
    estimands: design.estimands || [],
    estimandDetails: extractEstimandDetails(studyVersion, design),
    treatmentTable: extractTreatmentTable(studyVersion, design, spineExt),
    products: (studyVersion.administrableProducts || []).map((p) => describeProduct(p, spineExt)),
    intercurrentEventsHandling: extractIntercurrentEventsHandling(design),
    analysisPopulations: (design.analysisPopulations || [])
      .map((p) => (p.text && (p.label || p.name) ? `${p.label || p.name}: ${p.text}` : p.text || p.name))
      .filter(Boolean),
    abbreviations: studyVersion.abbreviations || [],
    referenceIdentifiers: studyVersion.referenceIdentifiers || [],
    biospecimenRetentions: design.biospecimenRetentions || [],
    indications: (design.indications || []).map((i) => i.description || i.name).filter(Boolean),
    activities: (design.activities || []).map((a) => ({
      name: a.name || a.label,
      description: a.description || null,
      // Procedure.procedureType is a plain string per the USDM export schema
      // (not a Code) — decode(p.code) is the coded variant, procedureType the free-text one.
      procedures: (a.definedProcedures || []).map((p) => p.procedureType || decode(p.code) || p.name).filter(Boolean),
    })),
    encounters: (design.encounters || []).map((e) => ({
      name: e.name || e.label,
      type: decode(e.type),
    })),
    encounterCount: (design.encounters || []).length,
    activityCount: (design.activities || []).length,
    // Rich, pre-built SoA table(s) — extension:soaGrid (spineExt['schedule.soa.grid'])
    // is a Dizzaroo-specific export of the exact same {tableId, columns (with
    // tiered headers), rowGroups, grid, footnotes} shape the S9 Assessment
    // Schedule module's own SoaTableRenderer consumes, so when present this
    // lets USDM-generated content render through that same rich grouped/
    // tiered-header component instead of the flatter reconstruction below.
    // null when the upload has no such extension (e.g. a non-Dizzaroo USDM
    // export) — callers fall back to `scheduleOfActivities` in that case.
    soaTables: Array.isArray(spineExt['schedule.soa.grid']?.tables)
      ? spineExt['schedule.soa.grid'].tables
      : null,
    // Real encounter x activity grid for "1.3 SCHEDULE OF ACTIVITIES" — see
    // extractScheduleOfActivities's own comment. Prefers the same soaTables
    // data (via s9TableToLegacyPayload, the same converter the S9 export
    // pipeline uses) when available, since it carries the authored visit
    // windows/footnotes; falls back to reconstructing from scheduleTimelines
    // when the upload has neither. null when there's no real schedule data
    // to build from at all.
    scheduleOfActivities: (() => {
      const tables = Array.isArray(spineExt['schedule.soa.grid']?.tables)
        ? spineExt['schedule.soa.grid'].tables
        : null;
      if (tables?.length) {
        const primaryTable = tables.find((t) => !t?.inheritsFromTableId) || tables[0];
        const legacy = s9TableToLegacyPayload(primaryTable, tables);
        if (legacy.visits?.length && legacy.sections?.length) return legacy;
      }
      return extractScheduleOfActivities(design, studyVersion.bcCategories);
    })(),
    scheduleNarrative,
    statisticalMethodsNarrative: narrativeByName['statistical methods'] || null,
    translationalResearchNarrative: narrativeByName['translational research plan'] || null,
    // Every narrative item, including ones without a named field above
    // (e.g. "Country selection rationale").
    narratives: (studyVersion.narrativeContentItems || [])
      .filter((n) => n?.text)
      .map((n) => ({ name: n.name || n.label || 'Narrative', text: n.text })),
    exportNotes: {
      notFinal: Array.isArray(usdmDoc?.['dizzaroo:exportReport']?.notFinal)
        ? usdmDoc['dizzaroo:exportReport'].notFinal.filter((n) => typeof n === 'string')
        : [],
    },
    designDetail,
    // Convenience pulls from the flattened extension map for the small set
    // of narrative-ish spine keys a Dizzaroo-produced export commonly
    // carries — still just a subset view of spineExt, not new data.
    // Current exports carry committees as committee.<type> and the disease
    // background inside Study Rationale 2.1; the older keys are kept first.
    committeeStructure:
      spineExt['safety.committee.structure'] ||
      spineExt['safety.dsmb.charter_framework'] ||
      designDetail.committees ||
      null,
    riskMitigationTriggers: spineExt['safety.risk_mitigation.triggers'] || null,
    diseaseRationale: spineExt['rationale.disease.epidemiology'] || designDetail.studyRationale?.['2.1'] || null,
    standardOfCareLandscape: spineExt['rationale.landscape.approved_therapies'] || null,
    efficacyClaim: spineExt['tpp.efficacy.primary_claim'] || null,
    spineExt,
  };
}

/** Best-effort extraction from the thinner usdm-schema.json catalogue shape. */
function extractFactsFromCatalogue(raw) {
  const classes = Array.isArray(raw?.classes) ? raw.classes : [];
  if (!classes.length) return null;

  const byCls = {};
  for (const entry of classes) {
    if (!entry?.cls) continue;
    const attrs = {};
    for (const a of entry.attributes || []) {
      if (a?.name !== undefined && a?.value !== undefined) attrs[a.name] = a.value;
    }
    let instances = Array.isArray(entry.instances) ? entry.instances : [];
    if (instances.length === 0 && Object.keys(attrs).length > 0) instances = [attrs];
    byCls[entry.cls] = instances;
  }

  const objectives = byCls.Objective || [];
  const objectivesByBucket = { primary: [], secondary: [], key_secondary: [], exploratory: [] };
  for (const obj of objectives) {
    const bucket = objectiveLevelBucket(obj);
    if (objectivesByBucket[bucket]) objectivesByBucket[bucket].push(obj);
    else objectivesByBucket.secondary.push(obj);
  }
  const arms = byCls.StudyArm || [];
  const design = (byCls.StudyDesign || byCls.InterventionalStudyDesign || [])[0] || {};

  return {
    identity: {},
    roles: [],
    armDetails: [],
    studyPeriods: [],
    studyDuration: null,
    estimandDetails: [],
    products: [],
    scheduleNarrative: {},
    narratives: [],
    exportNotes: { notFinal: [] },
    designDetail: {},
    design: {
      studyType: decode(design.studyType),
      studyPhase: decode(design.studyPhase?.standardCode) || decode(design.studyPhase),
      model: decode(design.model),
      // AliasCode, same as studyPhase — the real value is under .standardCode.
      blindingSchema: decode(design.blindingSchema?.standardCode) || decode(design.blindingSchema),
      intentTypes: [],
      rationale: design.rationale || null,
    },
    arms,
    investigationalArmLabel: arms[0]?.name || null,
    controlArmLabel: arms[1]?.name || null,
    interventionNames: (byCls.StudyIntervention || []).map((iv) => iv.name).filter(Boolean),
    population: {
      description: (byCls.PopulationDefinition || byCls.StudyDesignPopulation || [])[0]?.description || null,
      plannedEnrollmentNumber:
        (byCls.PopulationDefinition || byCls.StudyDesignPopulation || [])[0]?.plannedEnrollmentNumber?.value ?? null,
      plannedAge: null,
      plannedSex: [],
      includesHealthySubjects: null,
      cohorts: [],
      eligibilityText: (byCls.EligibilityCriterionItem || []).map((i) => i.text).filter(Boolean).join('\n'),
    },
    objectivesByBucket,
    estimands: byCls.Estimand || [],
    treatmentTable: [],
    intercurrentEventsHandling: (byCls.IntercurrentEvent || []).map((e) => ({ event: e.name || e.text, strategy: e.strategy || e.text })),
    analysisPopulations: (byCls.AnalysisPopulation || []).map((p) => p.text).filter(Boolean),
    abbreviations: byCls.Abbreviation || [],
    referenceIdentifiers: byCls.ReferenceIdentifier || [],
    biospecimenRetentions: byCls.BiospecimenRetention || [],
    indications: (byCls.Indication || []).map((i) => i.description || i.name).filter(Boolean),
    activities: (byCls.Activity || []).map((a) => ({ name: a.name || a.label, description: a.description || null, procedures: [] })),
    encounters: (byCls.Encounter || []).map((e) => ({ name: e.name || e.label, type: decode(e.type) })),
    encounterCount: (byCls.Encounter || []).length,
    activityCount: (byCls.Activity || []).length,
    // The catalogue shape never has ScheduleTimeline/ScheduledActivityInstance
    // classes — no relationship data exists to build a real SoA grid from.
    soaTables: null,
    scheduleOfActivities: null,
    statisticalMethodsNarrative: null,
    translationalResearchNarrative: null,
    committeeStructure: null,
    riskMitigationTriggers: null,
    diseaseRationale: null,
    standardOfCareLandscape: null,
    efficacyClaim: null,
    spineExt: {},
  };
}

/**
 * First spineExt value whose key is `suffix` or ends with `.${suffix}`.
 * Several Design Studio keys embed a study-specific id in the middle (e.g.
 * "population.provisional.group.<group_id>.eligibility_considerations"), so
 * an exact-key lookup would miss them.
 */
function findSpineExtBySuffix(spineExt, suffix, prefix = '') {
  if (!spineExt) return null;
  if (!prefix && spineExt[suffix] !== undefined) return spineExt[suffix];
  const key = Object.keys(spineExt).find((k) => k.startsWith(prefix) && k.endsWith(`.${suffix}`));
  return key ? spineExt[key] : null;
}

/**
 * Value of the first ExtensionAttribute anywhere in the export whose url ends
 * with `urlSuffix`. Needed where two extension classes share the same '#key'
 * (e.g. provisionalPopulation#population.provisional.indication and
 * decisionRationale#population.provisional.indication), which the flattened
 * spineExt map cannot tell apart.
 */
function findRawExtensionValue(node, urlSuffix, depth = 0) {
  if (!node || typeof node !== 'object' || depth > 40) return null;
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findRawExtensionValue(item, urlSuffix, depth + 1);
      if (found != null) return found;
    }
    return null;
  }
  if (typeof node.url === 'string' && node.url.endsWith(urlSuffix) && node.valueString != null) {
    try {
      return JSON.parse(node.valueString);
    } catch (e) {
      return node.valueString;
    }
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === 'object') {
      const found = findRawExtensionValue(value, urlSuffix, depth + 1);
      if (found != null) return found;
    }
  }
  return null;
}

function toStringList(value) {
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'string' ? v : v?.text || null)).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

// Design Studio parameters that feed each gap area. Used only to list which
// decisions are still open, from the export's own dizzaroo:parameterManifest.
const MANIFEST_GAP_PARAMETERS = {
  objectivesEndpoints: [
    'endpoint.primary.definition',
    'endpoint.key_secondary.list',
    'endpoint.additional_secondary.list',
    'endpoint.exploratory.list',
  ],
  eligibility: [
    'population.eligibility.disease_inclusion',
    'population.eligibility.performance_status',
    'population.eligibility.organ_function',
    'population.eligibility.washout_rules',
    'population.eligibility.disease_exclusion',
    'population.eligibility.general_exclusions',
    'population.eligibility.comorbidity_rules',
    'population.eligibility.concomitant_at_entry',
  ],
  estimands: ['stats.estimands.framework', 'stats.estimands.intercurrent_strategy', 'stats.analysis_populations'],
  sampleSize: [
    'stats.sample_size.target_n',
    'stats.sample_size.events_required',
    'stats.power.assumptions',
    'stats.power.effect_size',
    'stats.power.event_rate_assumptions',
    'stats.alpha.topology',
  ],
};

function manifestMissing(raw, parameterIds) {
  const manifest = Array.isArray(raw?.['dizzaroo:parameterManifest']) ? raw['dizzaroo:parameterManifest'] : [];
  if (!manifest.length) return [];
  const byId = new Map(manifest.map((p) => [p.parameterId, p]));
  return parameterIds.filter((id) => byId.has(id) && byId.get(id).hasValue === false);
}

/**
 * Adds two views the section prompts rely on to avoid inventing content:
 * - dataGaps: which of objectives/endpoints, eligibility, estimands and sample
 *   size have NO structured USDM data (each with the still-open Design Studio
 *   parameters, when the export carries a parameter manifest).
 * - provisional: Design Studio notes that are not confirmed design decisions
 *   but may be cited as provisional (eligibility considerations, TPP claim,
 *   randomization, primary analysis method).
 */
function attachGapsAndProvisional(facts, raw) {
  const spineExt = facts.spineExt || {};
  const anyObjectives = ['primary', 'key_secondary', 'secondary', 'exploratory'].some(
    (k) => facts.objectivesByBucket?.[k]?.length,
  );
  const hasPrimary = Boolean(facts.objectivesByBucket?.primary?.length);
  const hasEligibility = Boolean(facts.population?.eligibilityText);
  const hasEstimands = Boolean(facts.estimands?.length);
  const hasSampleSize = facts.population?.plannedEnrollmentNumber != null;

  facts.dataGaps = {
    objectivesEndpoints: {
      missing: !anyObjectives,
      primaryMissing: !hasPrimary,
      openParameters: manifestMissing(raw, MANIFEST_GAP_PARAMETERS.objectivesEndpoints),
    },
    eligibility: {
      missing: !hasEligibility,
      openParameters: manifestMissing(raw, MANIFEST_GAP_PARAMETERS.eligibility),
    },
    estimands: {
      missing: !hasEstimands,
      openParameters: manifestMissing(raw, MANIFEST_GAP_PARAMETERS.estimands),
    },
    sampleSize: {
      missing: !hasSampleSize,
      openParameters: manifestMissing(raw, MANIFEST_GAP_PARAMETERS.sampleSize),
    },
  };

  // Published Design Studio decisions that standard USDM has no field for, so
  // the export carries them as extensions. These are confirmed design, not
  // provisional notes, and are stated as facts.
  facts.designDecisions = {
    randomizationRatio: spineExt['design.randomization.ratio'] || null,
    stratificationFactors: spineExt['design.randomization.stratification_factors'] || null,
    primaryAnalysisMethod: spineExt['stats.primary_analysis.method'] || null,
  };

  // Working notes, only offered for an area that has no structured data —
  // otherwise they would compete with the real objectives/criteria.
  const indication = findRawExtensionValue(
    raw?.study?.versions,
    'provisionalPopulation#population.provisional.indication',
  );
  facts.provisional = {
    eligibilityConsiderations: hasEligibility
      ? []
      : toStringList(findSpineExtBySuffix(spineExt, 'eligibility_considerations', 'population.provisional.group.')),
    populationStatement: facts.population?.description
      ? null
      : findSpineExtBySuffix(spineExt, 'statement', 'population.provisional.group.') || null,
    indicationStatement: facts.indications?.length
      ? null
      : (indication && typeof indication === 'object' ? indication.indication_statement : indication) || null,
    primaryQuestion: anyObjectives ? null : spineExt['outline.intent.primary_question'] || null,
    primaryEfficacyClaim: anyObjectives ? null : spineExt['tpp.efficacy.primary_claim'] || null,
    secondaryEfficacyClaims: anyObjectives ? null : spineExt['tpp.efficacy.secondary_claims'] || null,
  };
  return facts;
}

/**
 * Human-readable list of data gaps for UI warnings (e.g. after fetching a
 * USDM export). Empty array when every area has structured data.
 */
export function getUsdmDataGapSummary(facts) {
  const gaps = facts?.dataGaps;
  if (!gaps) return [];
  const labels = {
    objectivesEndpoints: 'Objectives and endpoints',
    eligibility: 'Inclusion and exclusion criteria',
    estimands: 'Estimands',
    sampleSize: 'Sample size',
  };
  return Object.entries(labels)
    .filter(([key]) => gaps[key]?.missing)
    .map(([key, label]) => ({ key, label, openParameters: gaps[key].openParameters || [] }));
}

/**
 * Per-area view of what the export contains, for the USDM Template tab's
 * coverage panel. status: 'present' (structured USDM data), 'provisional'
 * (only Design Studio working notes), or 'missing'.
 */
export function getUsdmDataCoverage(facts) {
  if (!facts) return [];
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const obj = facts.objectivesByBucket || {};
  const objectiveCounts = [
    ['primary', 'primary'],
    ['key_secondary', 'key secondary'],
    ['secondary', 'secondary'],
    ['exploratory', 'exploratory'],
  ]
    .filter(([k]) => obj[k]?.length)
    .map(([k, label]) => `${obj[k].length} ${label}`);
  const endpointCount = Object.values(obj).flat().reduce((n, o) => n + (o?.endpoints?.length || 0), 0);
  // USDM-instance facts carry the counts; the catalogue shape only has text.
  const eligibilityText = facts.population?.eligibilityText || '';
  const inclusionCount = Number.isInteger(facts.population?.inclusionCount)
    ? facts.population.inclusionCount
    : (eligibilityText.split('Exclusion Criteria:')[0].match(/^- /gm) || []).length;
  const exclusionCount = Number.isInteger(facts.population?.exclusionCount)
    ? facts.population.exclusionCount
    : eligibilityText.includes('Exclusion Criteria:')
      ? (eligibilityText.split('Exclusion Criteria:')[1].match(/^- /gm) || []).length
      : 0;
  const prov = facts.provisional || {};
  const gaps = facts.dataGaps || {};
  const open = (key) => gaps[key]?.openParameters || [];

  const rows = [];
  const add = (key, label, status, detail, openParameters = []) =>
    rows.push({ key, label, status, detail, openParameters });

  add(
    'objectives',
    'Objectives and endpoints',
    objectiveCounts.length ? 'present' : prov.primaryEfficacyClaim || prov.primaryQuestion ? 'provisional' : 'missing',
    objectiveCounts.length
      ? `${objectiveCounts.join(', ')} objective(s); ${plural(endpointCount, 'endpoint')}`
      : prov.primaryEfficacyClaim
        ? `No objectives. Provisional target only: ${prov.primaryEfficacyClaim}`
        : 'No objectives or endpoints',
    open('objectivesEndpoints'),
  );
  add(
    'inclusion',
    'Inclusion criteria',
    inclusionCount ? 'present' : prov.eligibilityConsiderations?.length ? 'provisional' : 'missing',
    inclusionCount
      ? plural(inclusionCount, 'criterion').replace('criterions', 'criteria')
      : prov.eligibilityConsiderations?.length
        ? `No criteria. ${prov.eligibilityConsiderations.length} provisional eligibility considerations`
        : 'No criteria',
    inclusionCount ? [] : open('eligibility').filter((p) => !/exclusion|comorbidity|concomitant/.test(p)),
  );
  add(
    'exclusion',
    'Exclusion criteria',
    exclusionCount ? 'present' : 'missing',
    exclusionCount ? plural(exclusionCount, 'criterion').replace('criterions', 'criteria') : 'No criteria',
    exclusionCount ? [] : open('eligibility').filter((p) => /exclusion|comorbidity|concomitant/.test(p)),
  );
  add(
    'estimands',
    'Estimands',
    facts.estimands?.length ? 'present' : 'missing',
    facts.estimands?.length ? plural(facts.estimands.length, 'estimand') : 'No estimands',
    open('estimands'),
  );
  add(
    'sampleSize',
    'Sample size',
    facts.population?.plannedEnrollmentNumber != null ? 'present' : 'missing',
    facts.population?.plannedEnrollmentNumber != null
      ? `${facts.population.plannedEnrollmentNumber} planned participants`
      : 'No planned enrollment number or assumptions',
    open('sampleSize'),
  );
  add(
    'arms',
    'Arms',
    facts.arms?.length ? 'present' : 'missing',
    facts.arms?.length ? facts.arms.map((a) => a.label || a.name).filter(Boolean).join('; ') : 'No arms',
  );
  add(
    'interventions',
    'Study interventions',
    facts.interventionNames?.length ? 'present' : 'missing',
    facts.interventionNames?.length ? facts.interventionNames.join('; ') : 'No interventions',
  );
  add(
    'design',
    'Design (model, blinding)',
    facts.design?.model || facts.design?.blindingSchema ? 'present' : 'missing',
    [facts.design?.model, facts.design?.blindingSchema].filter(Boolean).join(', ') || 'No design model or blinding',
  );
  const decisions = facts.designDecisions || {};
  add(
    'randomization',
    'Randomization',
    decisions.randomizationRatio || decisions.stratificationFactors ? 'present' : 'missing',
    [
      decisions.randomizationRatio && `Ratio ${formatProvisionalValue(decisions.randomizationRatio)}`,
      decisions.stratificationFactors && `Stratified by: ${formatProvisionalValue(decisions.stratificationFactors)}`,
    ]
      .filter(Boolean)
      .join('. ') || 'No ratio or stratification',
  );
  add(
    'analysis',
    'Primary analysis method',
    decisions.primaryAnalysisMethod ? 'present' : 'missing',
    decisions.primaryAnalysisMethod ? formatProvisionalValue(decisions.primaryAnalysisMethod) : 'Not stated',
  );
  add(
    'soa',
    'Schedule of activities',
    facts.soaTables?.length || facts.scheduleOfActivities ? 'present' : 'missing',
    facts.soaTables?.length || facts.scheduleOfActivities
      ? `${facts.soaTables?.length ? `${plural(facts.soaTables.length, 'table')}, ` : ''}${plural(facts.encounterCount || 0, 'visit')}, ${plural(facts.activityCount || 0, 'activity').replace('activitys', 'activities')}`
      : 'No schedule',
  );
  return rows;
}

/**
 * Detects the upload shape and extracts a common fact set. Returns null if
 * neither a usable USDM instance nor a usable catalogue is found (caller
 * should treat this as "no data uploaded" and fall back to gap notices).
 */
export function normalizeUsdmCatalogue(raw) {
  if (raw?.study) {
    const facts = extractFactsFromUsdmInstance(raw);
    if (facts) return attachGapsAndProvisional(facts, raw);
  }
  const facts = extractFactsFromCatalogue(raw);
  return facts ? attachGapsAndProvisional(facts, raw) : null;
}

/**
 * Maps normalizeUsdmCatalogue()'s fact set onto the title page's own field
 * names (protocolTitlePageFields.js), for the projects that never went
 * through Clinical Intake / Administrative Info at all (USDM-catalogue-only
 * protocols) — see getTitlePageConfigWithData in DocumentEditor.jsx, which
 * uses this as a per-field fallback whenever the admin form has nothing.
 * Never fabricates: a key is simply absent when the upload has no source for
 * it (e.g. version/amendment/sponsor-signatory data, which USDM in practice
 * essentially never carries in this app's uploads).
 */
export function extractTitlePageFactsFromUsdm(raw) {
  const facts = normalizeUsdmCatalogue(raw);
  if (!facts) return null;

  // study.name is NOT usable as a title fallback: in every real Dizzaroo
  // catalogue export inspected, it's an internal UUID (e.g.
  // "4366e081-a196-478e-bfbe-de89c787f6fb"), not a human-readable title.
  // Only the USDM-standard titles[] array (officialTitle/briefTitle) is a
  // real title — when that's empty (the common case for this app's
  // exports), leave the field unset rather than surface the UUID.
  const out = {};
  if (facts.identity?.officialTitle) out.fullTitle = facts.identity.officialTitle;
  if (facts.identity?.briefTitle) out.shortTitle = facts.identity.briefTitle;
  if (facts.identity?.acronym) out.trialAcronym = facts.identity.acronym;
  if (facts.identity?.protocolNumber) out.sponsorProtocolIdentifier = facts.identity.protocolNumber;
  if (facts.identity?.nctNumber) out.nctNumber = facts.identity.nctNumber;
  if (facts.identity?.sponsorName) out.sponsorNameAddress = facts.identity.sponsorName;
  // Kept apart from the name: the name alone also feeds the confidentiality statement.
  if (facts.identity?.sponsorAddress) out.sponsorAddress = facts.identity.sponsorAddress;
  if (facts.design?.studyPhase) out.trialPhase = facts.design.studyPhase;
  return Object.keys(out).length ? out : null;
}

/**
 * Full plain-text rendering of `facts`, passed as the `context` parameter to
 * generateContent() for every USDM-sourced section. Some section prompts
 * (1, 12, 13, 14 — the ones with no {{facts.*}} tokens) were written against
 * the Clinical Intake flow's convention that `context` alone carries the
 * complete data ("use only the provided context") — a short identity-only
 * summary here left those sections with nothing to work from even though
 * the same data was present under the separate STUDY DEFINITION DATA
 * block. This mirrors etmf-client/src/sap/utils/usdmToSapContext.js's
 * formatUsdmFactsForPrompt() in spirit: every fact rendered, nothing
 * fabricated for what's absent.
 */
export function buildUsdmCatalogueGlobalContext(facts) {
  if (!facts) return '';
  const lines = [];

  if (facts.identity?.studyName || facts.identity?.officialTitle || facts.identity?.protocolNumber) {
    lines.push('STUDY IDENTITY');
    if (facts.identity.studyName) lines.push(`- Study name: ${facts.identity.studyName}`);
    if (facts.identity.officialTitle) lines.push(`- Official title: ${facts.identity.officialTitle}`);
    if (facts.identity.briefTitle) lines.push(`- Brief/short title: ${facts.identity.briefTitle}`);
    if (facts.identity.protocolNumber) lines.push(`- Protocol/study identifier: ${facts.identity.protocolNumber}`);
    if (facts.identity.sponsorName) lines.push(`- Sponsor: ${facts.identity.sponsorName}`);
    if (facts.identity.sponsorAddress) lines.push(`- Sponsor address: ${facts.identity.sponsorAddress}`);
    for (const r of facts.roles || []) {
      if (/^sponsor$/i.test(r.role || '') || !(r.label || r.role)) continue;
      lines.push(`- Study role — ${r.label || r.role}${r.description ? `: ${r.description}` : ''}`);
    }
    if (facts.identity.designVersion || facts.identity.designApprovalDate) {
      lines.push(
        `- Study design record: version ${facts.identity.designVersion || '(not stated)'}${facts.identity.designApprovalDate ? `, approved ${facts.identity.designApprovalDate}` : ''} (the Study Design Studio record — not the protocol version or protocol date)`,
      );
    }
    lines.push('');
  }

  if (facts.design?.studyPhase || facts.design?.studyType || facts.design?.model) {
    lines.push('STUDY DESIGN');
    if (facts.design.studyType) lines.push(`- Study type: ${facts.design.studyType}`);
    if (facts.design.studyPhase) lines.push(`- Phase: ${facts.design.studyPhase}`);
    if (facts.design.model) lines.push(`- Design model: ${facts.design.model}`);
    if (facts.design.blindingSchema) lines.push(`- Blinding: ${facts.design.blindingSchema}`);
    if (facts.design.intentTypes?.length) lines.push(`- Intent types: ${facts.design.intentTypes.join(', ')}`);
    if (facts.design.rationale) lines.push(`- Design rationale: ${facts.design.rationale}`);
    if (facts.interventionNames.length) lines.push(`- Interventions: ${facts.interventionNames.join(', ')}`);
    if (facts.studyDuration?.text) {
      lines.push(`- Planned study duration: ${facts.studyDuration.text}${facts.studyDuration.reason ? ` (${facts.studyDuration.reason})` : ''}`);
    }
    lines.push('');
  }

  if (facts.arms.length) {
    lines.push('ARMS');
    if (facts.armDetails?.length) {
      for (const a of facts.armDetails) {
        lines.push(
          `- ${a.name || '(unnamed)'}${a.type ? ` [${a.type}]` : ''}${a.description ? ` — ${a.description}` : ''}${a.regimens.length ? ` — regimen(s): ${a.regimens.join('; ')}` : ''}${a.cohorts.length ? ` — population: ${a.cohorts.join('; ')}` : ''}`,
        );
      }
    } else {
      for (const a of facts.arms) {
        lines.push(`- ${a.label || a.name || '(unnamed)'}${a.type ? ` [${decode(a.type)}]` : ''}${a.description ? ` — ${a.description}` : ''}`);
      }
    }
    if (facts.investigationalArmLabel) lines.push(`- Investigational arm: ${facts.investigationalArmLabel}`);
    if (facts.controlArmLabel) lines.push(`- Control arm: ${facts.controlArmLabel}`);
    lines.push('');
  }

  if (facts.studyPeriods?.length) {
    lines.push('STUDY PERIODS (in order)');
    for (const period of facts.studyPeriods) {
      lines.push(`- ${period.name}${period.type ? ` [${period.type}]` : ''}${period.plannedDuration ? ` — planned duration: ${period.plannedDuration}` : ''}`);
      for (const el of period.elements) {
        const rules = [el.start && `starts at: ${el.start}`, el.end && `ends at: ${el.end}`].filter(Boolean).join('; ');
        lines.push(`    - ${el.name}${el.arms.length ? ` (arm: ${el.arms.join('; ')})` : ''}${rules ? ` — ${rules}` : ''}`);
      }
    }
    lines.push('');
  }

  if (facts.treatmentTable.length || facts.products?.length) {
    lines.push('TREATMENT / ADMINISTRATION');
    for (const row of facts.treatmentTable) {
      const name = row.product ? `${row.product} (${row.intervention})` : row.intervention;
      const parts = [
        row.dose,
        row.route,
        row.frequency,
        row.schedule && row.schedule !== row.frequency ? `schedule: ${row.schedule}` : null,
        row.dosingDays && row.dosingDays !== 'continuous' && !String(row.schedule || '').includes(row.dosingDays)
          ? `dosing days: ${row.dosingDays}`
          : null,
        row.cycleLengthDays ? `${row.cycleLengthDays}-day cycle` : null,
        row.doseForm,
        row.duration,
      ].filter(Boolean);
      lines.push(`- ${name}${row.arm ? ` [arm: ${row.arm}]` : ''}${parts.length ? `: ${parts.join(', ')}` : ''}`);
    }
    if (facts.products?.length) {
      const products = facts.products
        .map((p) => `${p.name}${[p.doseForm, p.pharmacologicClass, p.designation].filter(Boolean).length ? ` (${[p.doseForm, p.pharmacologicClass, p.designation].filter(Boolean).join('; ')})` : ''}`)
        .join('; ');
      lines.push(`- Products: ${products}`);
    }
    lines.push('');
  }

  if (
    facts.population?.description ||
    facts.population?.eligibilityText ||
    facts.population?.plannedEnrollmentNumber != null ||
    facts.population?.cohorts?.length
  ) {
    lines.push('POPULATION');
    if (facts.population.description) lines.push(`- Description: ${facts.population.description}`);
    if (facts.population.plannedEnrollmentNumber != null) lines.push(`- Planned enrollment: ${facts.population.plannedEnrollmentNumber}`);
    if (facts.population.plannedAge) lines.push(`- Planned age: ${facts.population.plannedAge}`);
    if (facts.population.plannedSex?.length) lines.push(`- Planned sex: ${facts.population.plannedSex.join(', ')}`);
    if (facts.population.includesHealthySubjects != null) {
      lines.push(`- Includes healthy participants: ${facts.population.includesHealthySubjects ? 'Yes' : 'No'}`);
    }
    if (facts.indications.length) lines.push(`- Indication(s): ${facts.indications.join('; ')}`);
    for (const c of facts.population.cohorts || []) {
      const detail = [
        c.characteristics.length && `characteristics: ${c.characteristics.join('; ')}`,
        c.indications.length && `indication: ${c.indications.join('; ')}`,
      ].filter(Boolean).join(' — ');
      lines.push(`- Cohort: ${c.name}${detail ? ` — ${detail}` : ''}`);
    }
    if (facts.population.eligibilityText) lines.push(`- Eligibility criteria:\n${facts.population.eligibilityText}`);
    lines.push('');
  }

  const anyObjectives = ['primary', 'key_secondary', 'secondary', 'exploratory'].some(
    (k) => facts.objectivesByBucket[k]?.length,
  );
  if (anyObjectives) {
    lines.push('OBJECTIVES & ENDPOINTS');
    for (const bucket of ['primary', 'key_secondary', 'secondary', 'exploratory']) {
      for (const obj of facts.objectivesByBucket[bucket] || []) {
        lines.push(`- [${bucket}] ${obj.text || obj.description || '(no text provided)'}`);
        for (const ep of obj.endpoints || []) {
          lines.push(`    - Endpoint: ${ep.text || '(no text provided)'}`);
        }
      }
    }
    lines.push('');
  }

  if (facts.estimandDetails?.length) {
    lines.push('ESTIMANDS');
    for (const est of facts.estimandDetails) {
      lines.push(`- ${est.name || '(unnamed)'}`);
      if (est.description) lines.push(`    - Description: ${est.description}`);
      if (est.variable) lines.push(`    - Variable (endpoint): ${est.variable}`);
      if (est.analysisPopulation) lines.push(`    - Analysis population: ${est.analysisPopulation}`);
      else if (est.populationSummary) lines.push(`    - Population summary: ${est.populationSummary}`);
      if (est.interventions.length) lines.push(`    - Treatments: ${est.interventions.join('; ')}`);
      for (const ice of est.intercurrentEvents) {
        lines.push(`    - Intercurrent event: ${ice.event || '(not described)'} — strategy: ${ice.strategy || '(not specified)'}`);
      }
    }
    lines.push('');
  } else if (facts.estimands.length) {
    lines.push('ESTIMANDS');
    for (const est of facts.estimands) {
      lines.push(`- ${est.name || '(unnamed)'}: ${est.populationSummary || '(not specified)'}`);
      for (const ice of est.intercurrentEvents || []) {
        lines.push(`    - Intercurrent event: ${ice.text || ice.name || '(unnamed)'} — strategy: ${ice.strategy || '(not specified)'}`);
      }
    }
    lines.push('');
  }

  if (facts.analysisPopulations.length) {
    lines.push('ANALYSIS POPULATIONS');
    for (const p of facts.analysisPopulations) lines.push(`- ${p}`);
    lines.push('');
  }

  const scheduleNarrative = Object.entries(facts.scheduleNarrative || {});
  if (facts.activities?.length || facts.encounters?.length || scheduleNarrative.length) {
    lines.push('SCHEDULE');
    for (const [key, text] of scheduleNarrative) {
      const label = key
        .replace(/^schedule\./, '')
        .split('.')
        .map((s) => s.replace(/_/g, ' ').replace(/\bpk\b/i, 'PK'))
        .join(' — ');
      lines.push(`- ${label.charAt(0).toUpperCase()}${label.slice(1)}: ${text}`);
    }
    if (facts.encounters?.length) lines.push(`- Encounters: ${facts.encounters.map((e) => e.name).filter(Boolean).join(', ')}`);
    if (facts.activities?.length) lines.push(`- Activities: ${facts.activities.map((a) => a.name).filter(Boolean).join(', ')}`);
    lines.push('');
  }

  if (facts.abbreviations.length) {
    lines.push('ABBREVIATIONS');
    for (const ab of facts.abbreviations) lines.push(`- ${ab.abbreviatedText}: ${ab.expandedText}`);
    lines.push('');
  }

  if (facts.referenceIdentifiers.length) {
    lines.push('REFERENCE IDENTIFIERS');
    for (const r of facts.referenceIdentifiers) lines.push(`- ${r.text}`);
    lines.push('');
  }

  if (facts.biospecimenRetentions.length) {
    lines.push('BIOSPECIMEN RETENTION');
    for (const b of facts.biospecimenRetentions) lines.push(`- ${b.name}: retained=${b.isRetained}`);
    lines.push('');
  }

  if (facts.statisticalMethodsNarrative) {
    lines.push('STATISTICAL METHODS NARRATIVE');
    lines.push(facts.statisticalMethodsNarrative);
    lines.push('');
  }

  if (facts.translationalResearchNarrative) {
    lines.push('TRANSLATIONAL RESEARCH NARRATIVE');
    lines.push(facts.translationalResearchNarrative);
    lines.push('');
  }

  for (const n of facts.narratives || []) {
    if (/^(statistical methods|translational research plan)$/i.test(n.name)) continue;
    lines.push(`${n.name.toUpperCase()} NARRATIVE`);
    lines.push(n.text);
    lines.push('');
  }

  if (facts.exportNotes?.notFinal?.length) {
    lines.push('NOT FINAL IN STUDY DESIGN (proposed at publication; describe these as proposed, not confirmed)');
    for (const note of facts.exportNotes.notFinal) lines.push(`- ${note}`);
    lines.push('');
  }

  const decisionLines = buildDesignDecisionLines(facts.designDecisions);
  if (decisionLines.length) {
    lines.push('DESIGN DECISIONS (published in Study Design Studio; carried as export extensions — state these as confirmed design, not as provisional)');
    lines.push(...decisionLines);
    lines.push('');
  }

  const provisionalLines = buildProvisionalLines(facts.provisional);
  if (provisionalLines.length) {
    lines.push('PROVISIONAL DESIGN INPUTS (Design Studio working notes — NOT confirmed design decisions)');
    lines.push('- Use these only where a section needs them, and label them as provisional in the text (e.g. "Provisionally, ...").');
    lines.push('- Do not turn them into additional criteria, endpoints, thresholds or numbers beyond what is written here.');
    lines.push(...provisionalLines);
    lines.push('');
  }

  const gapLines = buildDataGapLines(facts.dataGaps);
  if (gapLines.length) {
    lines.push('DATA NOT YET DEFINED IN STUDY DESIGN');
    lines.push('- The study design export has no structured data for the items below. For each, write "Not yet defined in Study Design." Do NOT invent content, and do NOT copy values from examples in the section instructions.');
    lines.push(...gapLines);
    lines.push('');
  }

  return lines.join('\n').trim();
}

function formatProvisionalValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(formatProvisionalValue).filter(Boolean).join('; ');
  return JSON.stringify(value);
}

function buildProvisionalLines(provisional) {
  if (!provisional) return [];
  const out = [];
  if (provisional.primaryQuestion) out.push(`- Primary study question: ${formatProvisionalValue(provisional.primaryQuestion)}`);
  if (provisional.populationStatement) out.push(`- Population statement: ${formatProvisionalValue(provisional.populationStatement)}`);
  if (provisional.indicationStatement) out.push(`- Proposed indication: ${formatProvisionalValue(provisional.indicationStatement)}`);
  if (provisional.eligibilityConsiderations?.length) {
    out.push('- Eligibility considerations (provisional; these are NOT a complete inclusion/exclusion list):');
    for (const item of provisional.eligibilityConsiderations) out.push(`    - ${item}`);
  }
  if (provisional.primaryEfficacyClaim) out.push(`- Target product profile primary efficacy claim: ${formatProvisionalValue(provisional.primaryEfficacyClaim)}`);
  if (provisional.secondaryEfficacyClaims) out.push(`- Target product profile secondary efficacy claims: ${formatProvisionalValue(provisional.secondaryEfficacyClaims)}`);
  return out;
}

function buildDesignDecisionLines(decisions) {
  if (!decisions) return [];
  const out = [];
  if (decisions.randomizationRatio) out.push(`- Randomization ratio: ${formatProvisionalValue(decisions.randomizationRatio)}`);
  if (decisions.stratificationFactors) out.push(`- Stratification factors: ${formatProvisionalValue(decisions.stratificationFactors)}`);
  if (decisions.primaryAnalysisMethod) out.push(`- Primary analysis method: ${formatProvisionalValue(decisions.primaryAnalysisMethod)}`);
  return out;
}

function buildDataGapLines(dataGaps) {
  if (!dataGaps) return [];
  const out = [];
  // The open Design Studio parameter ids stay out of the model's text: it
  // copied them ("stats.estimands.framework, ...") into protocol sections.
  // The coverage panel still lists them, from facts.dataGaps.
  const withOpen = (label) => label;
  if (dataGaps.objectivesEndpoints?.missing) {
    out.push(`- ${withOpen('Objectives and endpoints (primary, key secondary, secondary, exploratory)', dataGaps.objectivesEndpoints)}`);
  } else if (dataGaps.objectivesEndpoints?.primaryMissing) {
    out.push('- Primary objective and primary endpoint');
  }
  if (dataGaps.eligibility?.missing) {
    out.push(`- ${withOpen('Inclusion criteria and exclusion criteria', dataGaps.eligibility)}`);
  }
  if (dataGaps.estimands?.missing) {
    out.push(`- ${withOpen('Estimands and intercurrent event strategies', dataGaps.estimands)}`);
  }
  if (dataGaps.sampleSize?.missing) {
    out.push(`- ${withOpen('Sample size (number of participants, number of events, power, alpha, effect size, dropout assumptions)', dataGaps.sampleSize)}`);
  }
  return out;
}

/**
 * Best-effort fetch of this project's Template Language data — never
 * derivable from USDM data itself (see this file's header comment:
 * template_language_fields.* has no USDM source), so it has to come from a
 * separate fetch. Returns null on any error (including "none configured"),
 * same contract as templateLanguageService.getTemplateLanguageDataByProject
 * itself.
 */
export async function fetchTemplateLanguageForUsdm(projectId) {
  try {
    return await templateLanguageService.getTemplateLanguageDataByProject(projectId);
  } catch (err) {
    console.error('fetchTemplateLanguageForUsdm: failed to load template language data:', err);
    return null;
  }
}

/**
 * Appends the study's ENTIRE Template Language table to `baseContext`, in
 * the same text format getPredefinedSections() uses (sectionData.js) — or
 * returns baseContext unchanged if there's no content at all.
 *
 * Deliberately not filtered to one section title: Template Language rows are
 * authored at ICH M11 subsection granularity ("7.1 Permanent discontinuation
 * of Trial Intervention", "9.2.1 Timing", ...), while USDM's own section list
 * (usdmProtocolSectionData.js) only has whole top-level sections ("7.
 * PARTICIPANT DISCONTINUATION...", "9. ADVERSE EVENTS..."). An exact-title
 * match between the two would miss nearly everything even when content is
 * genuinely configured. This instead mirrors what getPredefinedSections()
 * itself actually does for the Clinical Intake path — it doesn't do
 * per-section title matching either; it dumps the whole table into a single
 * shared `context` string used identically for every generated section
 * (`sectionsWithContext = DEFAULT_SECTIONS.map(section => ({...section,
 * context: enhancedContext}))`, sectionData.js). The USDM path keeps this
 * per-section (via each section's own "Use Template" checkbox in
 * UsdmTemplateTab.jsx) — the only piece Clinical Intake DOESN'T have — but
 * once a section opts in, it gets the same complete dump, not a filtered one.
 */
export function mergeTemplateLanguageBlock(baseContext, templateLanguageData) {
  const entries = Object.entries(templateLanguageData?.data || {}).filter(
    ([, entry]) => entry?.content || entry?.userSuggestion
  );
  if (!entries.length) return baseContext;

  const lines = ['', '--- TEMPLATE LANGUAGE SETTINGS ---'];
  if (templateLanguageData.template) lines.push(`Selected Template: ${templateLanguageData.template}`, '');
  for (const [title, entry] of entries) {
    if (entry.content) lines.push(`${title}:`, entry.content, '');
    if (entry.userSuggestion) lines.push(`${title} - User Suggestion:`, entry.userSuggestion, '');
  }

  return `${baseContext}\n${lines.join('\n')}`.trim();
}
